import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, Target, Users, Globe, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: CheckCircle2,
      title: 'Quality First',
      description: 'We never compromise on quality. Every product undergoes rigorous quality checks before export.'
    },
    {
      icon: Users,
      title: 'Customer Focus',
      description: 'Building lasting relationships through exceptional service and timely communication.'
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Expanding our footprint across continents while maintaining local authenticity.'
    },
    {
      icon: TrendingUp,
      title: 'Continuous Growth',
      description: 'Constantly improving our processes, products, and services for better results.'
    }
  ];

  const milestones = [
    { year: '2025', event: 'Company Founded', description: 'Started operations in Pune, Maharashtra' },
    { year: '2025', event: 'Certifications Obtained', description: 'Secured FSSAI, IEC, and APEDA registrations' },
    { year: '2025', event: 'Partner Network', description: 'Established partnerships with certified facilities' },
    { year: '2025', event: 'Product Range', description: 'Curated portfolio of 4 premium products' },
    { year: '2025', event: 'Market Research', description: 'Identified international markets for expansion' },
    { year: '2025', event: 'Launch Preparation', description: 'Preparing for first international shipment' }
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
        <title>About Us – Agro Corp India | Premium Agricultural Exporter from Pune</title>
        <meta name="description" content="Agro Corp India, established 2025 in Pune, Maharashtra. We export premium agricultural products — jaggery, basmati rice, dehydrated products — to international markets worldwide." />
        <meta name="keywords" content="about Agro Corp India, agricultural exporter Pune, food export company India, B2B agro supplier" />
        <link rel="canonical" href="https://www.agrocorpindia.com/about" />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative h-[60vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.unsplash.com/photo-1549024449-d6968d2a435f')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            About Agro Corp India
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Your trusted partner for quality agricultural exports, established in 2025
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider bg-amber-50 px-4 py-2 rounded-full inline-block">
                Our Story
              </span>
              <h2 className="text-4xl font-bold text-green-900 leading-tight">
                Building Trust Through Quality
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Agro Corp India was established in 2025 in Pune, Maharashtra, with a vision to supply high-quality natural and processed agricultural products to global markets. As a new entrant in the export industry, we are currently preparing for our first international shipment. We collaborate with certified partner facilities for storage, processing, and packaging to ensure product quality and compliance with international standards.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We work with certified partner storage, processing, and packaging facilities in Pune and surrounding regions to maintain quality and compliance. Our team is focused on building lasting partnerships based on trust and consistent delivery of premium agricultural products.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1589313331637-fa8ceb1fac0d"
                alt="Agricultural operations"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="p-8 border-2 border-green-200 hover:shadow-xl transition-all duration-300">
              <Target className="w-12 h-12 text-green-700 mb-4" />
              <h3 className="text-2xl font-bold text-green-900 mb-4">Our Mission</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                To deliver premium quality agricultural products to global markets while maintaining the highest standards of purity, hygiene, and sustainability. We aim to be the preferred export partner for businesses seeking reliable supply chains and consistent quality.
              </p>
            </Card>
            <Card className="p-8 border-2 border-amber-200 hover:shadow-xl transition-all duration-300">
              <Globe className="w-12 h-12 text-amber-600 mb-4" />
              <h3 className="text-2xl font-bold text-green-900 mb-4">Our Vision</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                To become a leading global exporter of Indian agricultural products, recognized for excellence in quality and commitment to customer satisfaction. We envision expanding our reach to new markets while building our own infrastructure and staying true to our core values.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider bg-amber-50 px-4 py-2 rounded-full inline-block mb-4">
              Our Values
            </span>
            <h2 className="text-4xl font-bold text-green-900 mb-4">
              What Drives Us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our core values guide every decision and action we take
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card
                  key={index}
                  className="text-center p-8 hover:shadow-xl transition-all duration-300 border-2 hover:border-green-200 group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-8 h-8 text-green-700" />
                  </div>
                  <h3 className="text-xl font-bold text-green-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline/Milestones */}
      <section className="py-20 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider bg-amber-50 px-4 py-2 rounded-full inline-block mb-4">
              Our Journey
            </span>
            <h2 className="text-4xl font-bold text-green-900 mb-4">
              Milestones & Achievements
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {milestones.map((milestone, index) => (
              <Card key={index} className="p-6 hover:shadow-xl transition-all duration-300 border-l-4 border-green-600">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-amber-400 to-amber-500 text-white font-bold px-4 py-2 rounded-lg text-lg">
                    {milestone.year}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-green-900 mb-2">{milestone.event}</h3>
                    <p className="text-gray-600">{milestone.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-700 to-green-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Let's Build a Partnership Together
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Experience the Agro Corp difference - quality products, reliable service, and competitive pricing
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                Get in Touch
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/products">
              <Button className="bg-white hover:bg-gray-100 text-green-800 px-8 py-4 text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                View Products
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
